/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { FormEvent, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { updateOrganizationNote } from "./queries";
import {
  Table,
  Icon,
  Loader,
  Button,
  TextArea,
  Transition,
  Modal,
  Dimmer,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "../../Components.module.css";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { Ico } from "../../Icon/Icon";
import ConfirmationDialog from "../../ConfirmationDialog";
import { parseGraphQlError } from "../../Authenticator/utils";

interface NotesProps {
  org: any;
  onRefresh?: any;
  ro: boolean;
}

const Notes: React.FC<NotesProps> = ({ org, onRefresh, ro }) => {
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [editNote, setEditNote] = useState<any>(null);
  const [editNoteText, setEditNoteText] = useState<string>("");

  const [updateNote, { loading: updateNoteLoading }] = useMutation(
    updateOrganizationNote,
    {
      onError: (e) => {
        const gqlError = parseGraphQlError(e);
        setMessage({
          title: "Update Note",
          message: `Unable to update note. ${gqlError ? gqlError + "." : ""}`,
        });
      },
      onCompleted: () => {
        setEditNote(null);
        setEditNoteText("");
        onRefresh && onRefresh();
      },
    }
  );

  const editNoteDialog = () => {
    return (
      <Transition transitionOnMount animation="fade" duration="250">
        <Modal
          style={{ padding: "1rem" }}
          centered
          closeOnDimmerClick={false}
          closeOnEscape={false}
          size="tiny"
          open
        >
          <Dimmer active={updateNoteLoading}>
            <Loader active={updateNoteLoading} size="large">
              Updating note...
            </Loader>
          </Dimmer>

          <TextArea
            autoFocus
            placeholder="Edit note..."
            spellCheck="true"
            style={{
              maxWidth: "100%",
              minWidth: "100%",
              height: "10rem",
              outline: 0,
              padding: "1rem",
              lineHeight: "20px",
            }}
            className={cStyles.input}
            onChange={(event: FormEvent<HTMLTextAreaElement>) => {
              const { value } = event.currentTarget;
              setEditNoteText(value);
            }}
            value={editNoteText}
          />

          {editNote !== "new" && (
            <div
              style={{
                marginTop: "1rem",
                lineHeight: "20px",
                color: "#666",
                fontSize: "13px",
              }}
            >
              <div>{`Created by ${editNote.createdBy} at ${new Date(
                editNote.created
              ).toLocaleDateString(navigator.language)}`}</div>
              {editNote.updated && (
                <div>{`Last update by ${editNote.updatedBy} at ${new Date(
                  editNote.updated
                ).toLocaleDateString(navigator.language)}`}</div>
              )}
            </div>
          )}

          <div style={{ marginTop: "2rem", textAlign: "right" }}>
            <Button
              style={{ float: "left" }}
              secondary
              disabled={editNote === "new"}
              className={cStyles.buttonSecond}
              onClick={() => {
                updateNote({
                  variables: {
                    orgId: org.id,
                    noteId: editNote.id,
                    noteText: "",
                  },
                });
              }}
            >
              <Ico name="trash" style={{ marginRight: "10px" }} />
              Delete
            </Button>

            <Button
              style={{ marginRight: "2rem" }}
              secondary
              className={cStyles.buttonSecond}
              onClick={() => {
                setEditNote(null);
                setEditNoteText("");
              }}
            >
              <Ico name="cancel" style={{ marginRight: "10px" }} />
              Cancel
            </Button>

            <Button
              primary
              className={cStyles.buttonPrime}
              disabled={false}
              onClick={() => {
                updateNote({
                  variables: {
                    orgId: org.id,
                    ...(editNote !== "new" && { noteId: editNote.id }),
                    noteText: editNoteText,
                  },
                });
              }}
            >
              <Ico name="check" style={{ marginRight: "10px" }} />
              Save
            </Button>
          </div>
        </Modal>
      </Transition>
    );
  };

  return (
    <React.Fragment>
      <Table celled compact>
        <Table.Body>
          <Table.Row key="name">
            <Table.Cell style={{ fontWeight: "700" }}>
              <Icon name="write square" style={{ marginRight: "1rem" }} />
              Notes
            </Table.Cell>

            <Table.Cell width="1" verticalAlign="top" textAlign="center">
              <Icon
                name="sticky note"
                size="large"
                style={{ display: "inline" }}
                className={styles.myAccountLink}
                onClick={() => {
                  setEditNote("new");
                }}
                disabled={ro}
              />
            </Table.Cell>
          </Table.Row>

          {org.notes &&
            org.notes.map((note: any) => {
              return (
                <Table.Row key={note.id}>
                  <Table.Cell style={{ lineHeight: "25px" }}>
                    {note.text}
                  </Table.Cell>
                  <Table.Cell width="1" verticalAlign="top" textAlign="center">
                    <Icon
                      name="write square"
                      size="large"
                      style={{ display: "inline" }}
                      className={styles.myAccountLink}
                      onClick={() => {
                        setEditNoteText(note.text);
                        setEditNote(note);
                      }}
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>
      </Table>

      {editNote && editNoteDialog()}

      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </React.Fragment>
  );
};

export default Notes;
